import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, ExternalLink, Trash2, X } from "lucide-react";
import { CalendarConfig, CalendloConfig } from "@/components/forms/BlockConfig";
import { Field, TextIn, Toggle } from "@/components/forms/Field";
import { useMailboxes } from "@/pages/FormEditor";
import { useConfirm } from "@/components/ConfirmDialog";
import ImageInput from "@/components/forms/ImageInput";

const HI = "https://hi.thickandthin.media";
const TYPES = [["short_text", "Short text"], ["long_text", "Long text"], ["email", "Email"], ["phone", "Phone"], ["dropdown", "Dropdown"]];

function FieldsEditor({ fields, set }) {
  const upd = (i, p) => set(fields.map((f, j) => (j === i ? { ...f, ...p } : f)));
  return (
    <div className="space-y-2 border-t pt-3" data-testid="link-fields-editor">
      <div className="text-xs font-medium">Questions before booking</div>
      {fields.map((f, i) => {
        const fixed = ["name", "email"].includes(f.id);
        return (
          <div key={i} className="panel-2 p-2 space-y-1.5" data-testid={`link-q-${i}`}>
            <div className="flex gap-2 items-center">
              <input className="flex-1 px-2 py-1 rounded border text-sm" value={f.label} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`link-q-label-${i}`} />
              <select disabled={fixed} className="px-2 py-1 rounded border text-xs bg-transparent" value={f.type} onChange={(e) => upd(i, { type: e.target.value })} data-testid={`link-q-type-${i}`}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {!fixed && <button className="text-neutral-500 hover:text-red-400" onClick={() => set(fields.filter((_, j) => j !== i))} data-testid={`link-q-del-${i}`}><X size={14} /></button>}
            </div>
            <div className="flex gap-3 items-center text-[11px] text-neutral-500">
              <span className="font-mono">{`{{${f.id}}}`}</span>
              {!fixed && <Toggle checked={f.required} onChange={(v) => upd(i, { required: v })} label="Required" testid={`link-q-req-${i}`} />}
              {fixed && <span>always asked</span>}
            </div>
            {f.type === "dropdown" && <textarea rows={3} className="w-full px-2 py-1 rounded border text-xs font-mono" placeholder="One option per line" value={(f.options || []).join("\n")} onChange={(e) => upd(i, { options: e.target.value.split("\n") })} data-testid={`link-q-opts-${i}`} />}
          </div>
        );
      })}
      <button className="btn text-xs" onClick={() => set([...fields, { id: `q_${Math.random().toString(36).slice(2, 7)}`, label: "New question", type: "short_text", required: false }])} data-testid="link-q-add"><Plus size={12} />Add question</button>
    </div>
  );
}

function PageDesign({ b, set }) {
  const color = (k, def, label) => (
    <label className="flex items-center gap-2 text-xs">{label}
      <input type="color" value={b[k] || def} onChange={(e) => set({ [k]: e.target.value })} data-testid={`link-${k}`} />
      {b[k] && <button className="underline text-neutral-500" onClick={() => set({ [k]: "" })}>reset</button>}
    </label>
  );
  return (
    <div className="space-y-2 border-t pt-3" data-testid="link-page-design">
      <div className="text-xs font-medium">Page design</div>
      <Field label="Headline above the event name"><TextIn value={b.page_headline} onChange={(v) => set({ page_headline: v })} placeholder="Thick & Thin" testid="link-page-headline" /></Field>
      <div className="flex flex-wrap gap-4">{color("page_accent", "#e8e2d4", "Accent")}{color("page_bg", "#0d0d0c", "Background")}</div>
      <Field label="Logo"><ImageInput value={b.page_logo} onChange={(v) => set({ page_logo: v })} testid="link-page-logo" /></Field>
      <Field label="Your photo"><ImageInput value={b.page_photo} onChange={(v) => set({ page_photo: v })} testid="link-page-photo" /></Field>
      <Field label="Background image"><ImageInput value={b.page_bg_image} onChange={(v) => set({ page_bg_image: v })} testid="link-page-bg-image" /></Field>
    </div>
  );
}

function Editor({ link, onSaved, onClose }) {
  const [b, setB] = useState(link);
  const mailboxes = useMailboxes();
  const set = (p) => setB((x) => ({ ...x, ...p }));
  const save = async () => {
    try { const { data } = await api.put(`/booking-links/${b._id}`, b); setB(data); toast.success("Link saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  return (
    <div className="panel p-4 space-y-3 max-w-2xl" data-testid="link-editor">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium flex-1">Edit booking link</div>
        <button className="btn btn-primary text-xs" onClick={save} data-testid="link-save">Save</button>
        <button className="btn-ghost p-1" onClick={onClose} data-testid="link-editor-close"><X size={14} /></button>
      </div>
      <Field label="Link"><div className="flex items-center gap-1 text-sm"><span className="text-neutral-500">hi.thickandthin.media/</span><TextIn value={b.slug} onChange={(v) => set({ slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "") })} testid="link-slug" /></div></Field>
      <Toggle checked={b.active !== false} onChange={(v) => set({ active: v })} label="Link is live" testid="link-active" />
      <CalendloConfig b={b} set={set} />
      <CalendarConfig b={b} set={set} mailboxes={mailboxes} />
      <FieldsEditor fields={b.fields || []} set={(fields) => set({ fields })} />
      <PageDesign b={b} set={set} />
    </div>
  );
}

export default function BookingLinks() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [slug, setSlug] = useState("");
  const { confirm } = useConfirm();
  const load = () => api.get("/booking-links").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { const { data } = await api.post("/booking-links", { slug }); setSlug(""); await load(); setEdit(data); toast.success("Link created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create"); }
  };
  const del = async (l) => {
    if (!(await confirm({ title: `Delete /${l.slug}?`, body: "The link stops working. Meetings already booked stay on the calendar.", danger: true }))) return;
    await api.delete(`/booking-links/${l._id}`); toast.success("Link deleted"); if (edit?._id === l._id) setEdit(null); load();
  };
  return (
    <div className="p-2 md:p-4 space-y-4" data-testid="booking-links">
      <div className="panel p-4 flex flex-wrap items-center gap-2">
        <div className="text-sm font-medium mr-2">Create calendar link</div>
        <span className="text-sm text-neutral-500">hi.thickandthin.media/</span>
        <input className="px-2 py-1.5 rounded border text-sm w-48" placeholder="khush" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} onKeyDown={(e) => e.key === "Enter" && slug && create()} data-testid="link-new-slug" />
        <button className="btn btn-primary text-xs" disabled={!slug} onClick={create} data-testid="link-create"><Plus size={12} />Create</button>
      </div>
      {edit && <Editor key={edit._id} link={edit} onSaved={load} onClose={() => setEdit(null)} />}
      <div className="panel divide-y divide-[var(--border)]">
        {!rows ? <div className="p-4 text-xs text-neutral-500">Loading…</div> : !rows.length ? <div className="p-4 text-sm text-neutral-500" data-testid="links-empty">No booking links yet.</div> : rows.map((l) => (
          <div key={l._id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`link-row-${l.slug}`}>
            <button className="flex-1 min-w-[200px] text-left" onClick={() => setEdit(l)} data-testid={`link-edit-${l.slug}`}>
              <div className="text-sm font-medium">{l.event_name} <span className="text-neutral-500 font-normal">· {l.meeting_duration_min} min</span>{l.active === false && <span className="ml-2 pill text-[10px]">off</span>}</div>
              <div className="text-[11px] text-neutral-500 font-mono">hi.thickandthin.media/{l.slug} · {(l.calendar_emails || []).join(", ")}</div>
            </button>
            <button className="btn-ghost btn p-1.5" title="Copy link" onClick={() => { navigator.clipboard?.writeText(`${HI}/${l.slug}`); toast.success("Link copied"); }} data-testid={`link-copy-${l.slug}`}><Copy size={13} /></button>
            <a className="btn-ghost btn p-1.5" title="Open" href={`/b/${l.slug}`} target="_blank" rel="noreferrer" data-testid={`link-open-${l.slug}`}><ExternalLink size={13} /></a>
            <button className="btn-ghost btn p-1.5 hover:text-red-400" title="Delete" onClick={() => del(l)} data-testid={`link-delete-${l.slug}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
